import {
  Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { DeviceDetailsService } from '../device-details.service';
import { Constants } from '../util/constants';
import { parseSearchDate, tryAutoCompleteDate, SearchDateError } from '../util/search-date-parser';

@Directive({
  selector: '[appSearchDateKeyboard]',
  standalone: false,
  providers: [DatePipe]
})
export class SearchDateKeyboardDirective implements OnInit, OnChanges, OnDestroy {

  @Input() dateValue: Date;
  @Input() placeholder: string;
  @Input() minDate: Date;
  @Input() maxDate: Date;

  @Output() dateCommitted = new EventEmitter<Date>();
  @Output() dateTypingStarted = new EventEmitter<void>();
  @Output() calendarRequested = new EventEmitter<void>();

  private editMode = false;
  private focusViaClick = false;
  private isTabletOrMobile = false;
  private featureEnabled = Constants.ENABLE_SEARCH_KEYBOARD_DATES;
  private errorEl: HTMLElement | null = null;
  private errorTimeout: any;
  private deviceSub: Subscription;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private deviceDetailsService: DeviceDetailsService
  ) {}

  ngOnInit(): void {
    const input = this.el.nativeElement;

    this.deviceSub = this.deviceDetailsService.isTabletOrMobile().subscribe(val => {
      this.isTabletOrMobile = val;
      this.updateReadonly();
    });

    if (!this.featureEnabled) {
      this.renderer.setAttribute(input, 'readonly', '');
      return;
    }

    // Set initial display value
    this.updateDisplayValue();

    // --- Event Listeners ---

    this.renderer.listen(input, 'mousedown', () => {
      if (this.isDisabled()) return;
      this.focusViaClick = true;
      setTimeout(() => { this.focusViaClick = false; }, 100);
    });

    this.renderer.listen(input, 'focus', () => {
      if (this.isDisabled()) return;
      // Place cursor at start so a blinking caret is visible
      // (select() hides the caret behind the selection highlight)
      setTimeout(() => input.setSelectionRange(0, 0), 0);
      // Open calendar on keyboard-based focus (Tab), not on mouse click
      if (!this.editMode && !this.focusViaClick) {
        this.calendarRequested.emit();
      }
    });

    this.renderer.listen(input, 'input', () => {
      if (this.isDisabled()) return;
      if (!this.editMode) {
        this.editMode = true;
        this.dateTypingStarted.emit();
      }
      // Try auto-complete as the user types
      this.tryAutoComplete();
    });

    this.renderer.listen(input, 'click', (event: MouseEvent) => {
      if (this.isDisabled()) return;
      // Only stop propagation while user is actively typing so that
      // repositioning the cursor doesn't toggle the calendar.
      // When not in editMode, let clicks propagate so the calendar opens normally.
      if (this.editMode) {
        event.stopPropagation();
      }
    });

    this.renderer.listen(input, 'keydown', (event: KeyboardEvent) => {
      if (this.isDisabled()) return;

      // Block space key – the date format is DDMMMYYYY (no spaces)
      if (event.key === ' ') {
        event.preventDefault();
        return;
      }

      // Clear display value on first printable keystroke so placeholder disappears immediately
      if (!this.editMode && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        input.value = '';
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        if (this.editMode) {
          event.preventDefault();
          this.commitInput();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.restoreDisplay();
        input.blur();
      }
    });

    this.renderer.listen(input, 'blur', () => {
      if (this.isDisabled()) return;
      if (this.editMode) {
        this.commitInput();
      }
      this.editMode = false;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateValue']) {
      this.updateDisplayValue();
    }
  }

  ngOnDestroy(): void {
    if (this.deviceSub) {
      this.deviceSub.unsubscribe();
    }
    this.clearError();
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  private isDisabled(): boolean {
    return !this.featureEnabled || this.isTabletOrMobile;
  }

  private updateReadonly(): void {
    const input = this.el.nativeElement;
    if (this.isDisabled()) {
      this.renderer.setAttribute(input, 'readonly', '');
    } else {
      this.renderer.removeAttribute(input, 'readonly');
    }
  }

  private updateDisplayValue(): void {
    const input = this.el.nativeElement;
    if (this.dateValue) {
      input.value = this.datePipe.transform(this.dateValue, 'dd MMM yyyy') || '';
    } else {
      input.value = this.placeholder || '';
    }
  }

  private commitInput(): void {
    const input = this.el.nativeElement;
    const raw = input.value;

    // If the text matches current display, just restore (user didn't actually change it)
    const currentDisplay = this.dateValue
      ? this.datePipe.transform(this.dateValue, 'dd MMM yyyy')
      : this.placeholder;
    if (raw === currentDisplay) {
      this.editMode = false;
      return;
    }

    const result = parseSearchDate(raw, this.minDate, this.maxDate);
    if (result.date) {
      this.clearError();
      this.editMode = false;
      // Immediately show formatted date (don't wait for Angular change detection)
      input.value = this.datePipe.transform(result.date, 'dd MMM yyyy') || '';
      this.dateCommitted.emit(result.date);
      input.blur();
    } else {
      this.showError(result.error);
      this.restoreDisplay();
    }
  }

  private tryAutoComplete(): void {
    const input = this.el.nativeElement;
    const raw = input.value;

    const result = tryAutoCompleteDate(raw, this.minDate, this.maxDate);
    if (!result) return; // Not enough info yet, keep waiting

    if (result.date) {
      this.clearError();
      this.editMode = false;
      input.value = this.datePipe.transform(result.date, 'dd MMM yyyy') || '';
      this.dateCommitted.emit(result.date);
      input.blur();
    } else if (result.error) {
      this.showError(result.error);
      this.restoreDisplay();
    }
  }

  private restoreDisplay(): void {
    this.editMode = false;
    this.updateDisplayValue();
  }

  private showError(error: SearchDateError): void {
    this.clearError();

    const msg = error === 'OUT_OF_RANGE'
      ? this.translate.instant('search.dateOutOfRange')
      : this.translate.instant('search.invalidDateFormat');

    this.errorEl = this.renderer.createElement('div');
    this.renderer.addClass(this.errorEl, 'search-date-keyboard-error');
    const text = this.renderer.createText(msg);
    this.renderer.appendChild(this.errorEl, text);

    // Insert error adjacent to the input's parent (.date-display or .date-section)
    const parent = this.el.nativeElement.parentElement;
    if (parent) {
      this.renderer.appendChild(parent, this.errorEl);
    }

    // Auto-hide after 3 seconds
    this.errorTimeout = setTimeout(() => this.clearError(), 3000);
  }

  private clearError(): void {
    if (this.errorEl && this.errorEl.parentElement) {
      this.renderer.removeChild(this.errorEl.parentElement, this.errorEl);
    }
    this.errorEl = null;
  }
}
